import { Conversation, ConversationContent, ConversationScrollButton } from '@/components/ai-elements/conversation'
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
} from '@/components/ai-elements/prompt-input'
import { AssistantMessageView, SuggestedPrompts } from '@/components/dashboard/common/assistant/assistant-message'
import DeleteModal from '@/components/modal/delete-modal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useAssistant } from '@/hooks/use-assistant'
import { cn } from '@/lib/utils'
import { Edit, MessagesSquare, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

interface AssistantPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const formatRelative = (iso: string) => {
  const then = new Date(iso).getTime()
  const diffSec = Math.floor((Date.now() - then) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  return `${Math.floor(diffSec / 86400)}d ago`
}

// const models = [
//   {
//     id: 'gpt-4o',
//     name: 'GPT-4o',
//     chef: 'OpenAI',
//     chefSlug: 'openai',
//     providers: ['openai', 'azure'],
//   },
//   {
//     id: 'gpt-4o-mini',
//     name: 'GPT-4o Mini',
//     chef: 'OpenAI',
//     chefSlug: 'openai',
//     providers: ['openai', 'azure'],
//   },
//   {
//     id: 'claude-opus-4-20250514',
//     name: 'Claude 4 Opus',
//     chef: 'Anthropic',
//     chefSlug: 'anthropic',
//     providers: ['anthropic', 'azure', 'google', 'amazon-bedrock'],
//   },
//   {
//     id: 'claude-sonnet-4-20250514',
//     name: 'Claude 4 Sonnet',
//     chef: 'Anthropic',
//     chefSlug: 'anthropic',
//     providers: ['anthropic', 'azure', 'google', 'amazon-bedrock'],
//   },
//   {
//     id: 'gemini-2.0-flash-exp',
//     name: 'Gemini 2.0 Flash',
//     chef: 'Google',
//     chefSlug: 'google',
//     providers: ['google'],
//   },
// ]

export const AssistantPanel = ({ open, onOpenChange }: AssistantPanelProps) => {
  const {
    messages,
    isStreaming,
    banner,
    dismissBanner,
    conversationId,
    conversations,
    refreshConversations,
    loadConversation,
    deleteConversation,
    sendMessage,
    stop,
    reset,
    dispatchToolUse,
    setReaction,
  } = useAssistant()
  const [input, setInput] = useState('')
  const location = useLocation()
  const navigate = useNavigate()
  // const [useMicrophone, setUseMicrophone] = useState<boolean>(false)
  // const [useWebSearch, setUseWebSearch] = useState<boolean>(false)
  // const [modelSelectorOpen, setModelSelectorOpen] = useState(false)
  // const [model, setModel] = useState<string>(models[0].id)
  // const selectedModelData = models.find((m) => m.id === model)
  const [pendingDeleteConversation, setPendingDeleteConversation] = useState<{ id: string } | null>(null)
  const [deleteConversationLoading, setDeleteConversationLoading] = useState(false)
  const [conversationsMenuOpen, setConversationsMenuOpen] = useState(false)

  // Refresh the conversation list when the panel opens so the dropdown is
  // current.
  useEffect(() => {
    if (open) void refreshConversations()
  }, [open, refreshConversations])

  const submit = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || isStreaming) return
    setInput('')
    void sendMessage({ text: trimmed, currentPath: location.pathname })
  }

  const handleDispatchToolUse = (toolUseId: string) => {
    void dispatchToolUse({
      toolUseId,
      navigate: (path) => navigate(path),
      currentPath: location.pathname,
    })
    // Close the panel so the user can see the destination page; the
    // tool-result continuation streams in the background and is visible
    // when the user re-opens the assistant.
    onOpenChange(false)
  }

  const sendDisabled = !input.trim() || banner?.code === 'quota_exceeded' || isStreaming

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        showClose={false}
        className='flex h-full min-h-0 w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md'
      >
        <SheetHeader className='border-b border-border px-4 py-3'>
          <div className='flex items-center justify-between gap-2'>
            <div className='min-w-0'>
              <div className='flex items-center gap-2'>
                <SheetTitle>CISO Adapt AI Assistant</SheetTitle>
                <Badge
                  variant='outline'
                  className='border-amber-200 bg-amber-100 text-amber-800 text-[10px] font-medium tracking-wide uppercase dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-200'
                >
                  Beta
                </Badge>
              </div>
              <SheetDescription>Ask questions and get help with your work.</SheetDescription>
            </div>
            <div className='flex items-center gap-1'>
              <DropdownMenu open={conversationsMenuOpen} onOpenChange={setConversationsMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    className='cursor-pointer h-8 w-8 border border-border hover:bg-accent hover:text-accent-foreground'
                    type='button'
                    variant='secondary'
                    size='default'
                    title='Conversations'
                  >
                    <MessagesSquare className='h-4 w-4' />
                    <span className='sr-only'>Conversations</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-72'>
                  <DropdownMenuLabel>Conversations</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault()
                      reset()
                    }}
                    disabled={isStreaming}
                  >
                    <Edit className='h-4 w-4' />
                    New chat
                  </DropdownMenuItem>
                  {conversations.length > 0 && <DropdownMenuSeparator />}
                  {conversations.length === 0 ? (
                    <div className='px-2 py-2 text-xs text-muted-foreground'>No prior chats yet.</div>
                  ) : (
                    <div className='max-h-[calc(100vh-300px)] overflow-y-auto'>
                      {conversations.map((c) => (
                        <DropdownMenuItem
                          key={c.id}
                          className='flex items-center gap-2'
                          onSelect={(e) => {
                            e.preventDefault()
                            if (c.id !== conversationId) void loadConversation(c.id)
                          }}
                        >
                          <span className='flex-1 truncate text-sm'>
                            {c.title ?? '(untitled)'}
                            <span className='ml-2 text-[10px] text-muted-foreground'>
                              {formatRelative(c.updatedAt)}
                            </span>
                          </span>
                          <Button
                            type='button'
                            variant='ghost'
                            aria-label='Delete conversation'
                            onClick={(e) => {
                              e.stopPropagation()
                              setConversationsMenuOpen(false)
                              setPendingDeleteConversation({ id: c.id })
                            }}
                            className={cn('text-destructive p-0 h-4 w-4 ')}
                          >
                            <Trash2 className='size-[13px] text-destructive/80' />
                          </Button>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                className='cursor-pointer h-8 w-8 bg-destructive/8 text-destructive hover:bg-destructive/10 border border-destructive/10'
                type='button'
                variant='secondary'
                size='default'
                title='Close'
                onClick={() => onOpenChange(false)}
              >
                <X className='h-4 w-4' />
                <span className='sr-only'>Close</span>
              </Button>
            </div>
          </div>
        </SheetHeader>

        {banner ? (
          <div
            className={cn(
              'flex items-start gap-2 px-4 py-2 text-xs border-b border-border',
              banner.code === 'quota_exceeded'
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                : 'bg-destructive/10 text-destructive'
            )}
          >
            <p className='flex-1'>{banner.message}</p>
            <button type='button' onClick={dismissBanner} aria-label='Dismiss' className='opacity-70 hover:opacity-100'>
              <X className='h-3.5 w-3.5' />
            </button>
          </div>
        ) : null}

        {messages?.length === 0 ? (
          <div className='min-h-0 flex-1 overflow-y-auto p-4'>
            <SuggestedPrompts onSelect={submit} disabled={isStreaming} />
          </div>
        ) : (
          <Conversation className='min-h-0 flex-1 overflow-hidden py-4'>
            <ConversationContent className='pb-10'>
              {messages?.map((m) => (
                <AssistantMessageView
                  key={m.id}
                  message={m}
                  onDispatchToolUse={handleDispatchToolUse}
                  onReact={(id, r) => void setReaction(id, r)}
                />
              ))}
            </ConversationContent>
            <ConversationScrollButton className='bottom-24' />
          </Conversation>
        )}

        <div className='w-full px-4 pb-4'>
          <PromptInput
            onSubmit={(message) => {
              if (isStreaming) {
                stop()
                return
              }
              submit(message.text ?? input)
            }}
            // globalDrop
            // multiple
          >
            {/* <PromptInputHeader>
              <PromptInputAttachments>
                {(attachment) => <PromptInputAttachment data={attachment} />}
              </PromptInputAttachments>
            </PromptInputHeader> */}
            <PromptInputBody>
              <PromptInputTextarea onChange={(event) => setInput(event.target.value)} value={input} />
            </PromptInputBody>
            <PromptInputFooter className='justify-end'>
              {/* <PromptInputTools>
                <PromptInputActionMenu>
                  <PromptInputActionMenuTrigger />
                  <PromptInputActionMenuContent>
                    <PromptInputActionAddAttachments />
                  </PromptInputActionMenuContent>
                </PromptInputActionMenu>
                <PromptInputButton
                  onClick={() => setUseMicrophone(!useMicrophone)}
                  variant={useMicrophone ? 'default' : 'ghost'}
                >
                  <MicIcon size={16} />
                  <span className='sr-only'>Microphone</span>
                </PromptInputButton>
                <PromptInputButton
                  onClick={() => setUseWebSearch(!useWebSearch)}
                  variant={useWebSearch ? 'default' : 'ghost'}
                >
                  <GlobeIcon size={16} />
                  <span>Search</span>
                </PromptInputButton>
                <ModelSelector onOpenChange={setModelSelectorOpen} open={modelSelectorOpen}>
                  <ModelSelectorTrigger asChild>
                    <PromptInputButton>
                      {selectedModelData?.chefSlug && <ModelSelectorLogo provider={selectedModelData.chefSlug} />}
                      {selectedModelData?.name && <ModelSelectorName>{selectedModelData.name}</ModelSelectorName>}
                    </PromptInputButton>
                  </ModelSelectorTrigger>
                  <ModelSelectorContent>
                    <ModelSelectorInput placeholder='Search models...' />
                    <ModelSelectorList>
                      <ModelSelectorEmpty>No models found.</ModelSelectorEmpty>
                      {['OpenAI', 'Anthropic', 'Google'].map((chef) => (
                        <ModelSelectorGroup key={chef} heading={chef}>
                          {models
                            .filter((m) => m.chef === chef)
                            .map((m) => (
                              <ModelSelectorItem
                                key={m.id}
                                onSelect={() => {
                                  setModel(m.id)
                                  setModelSelectorOpen(false)
                                }}
                                value={m.id}
                              >
                                <ModelSelectorLogo provider={m.chefSlug} />
                                <ModelSelectorName>{m.name}</ModelSelectorName>
                                <ModelSelectorLogoGroup>
                                  {m.providers.map((provider) => (
                                    <ModelSelectorLogo key={provider} provider={provider} />
                                  ))}
                                </ModelSelectorLogoGroup>
                                {model === m.id ? (
                                  <CheckIcon className='ml-auto size-4' />
                                ) : (
                                  <div className='ml-auto size-4' />
                                )}
                              </ModelSelectorItem>
                            ))}
                        </ModelSelectorGroup>
                      ))}
                    </ModelSelectorList>
                  </ModelSelectorContent>
                </ModelSelector>
              </PromptInputTools> */}
              <PromptInputSubmit disabled={sendDisabled} status={isStreaming ? 'streaming' : undefined} />
            </PromptInputFooter>
          </PromptInput>
        </div>

        <DeleteModal
          open={pendingDeleteConversation !== null}
          setOpen={(next) => {
            if (!next) {
              setPendingDeleteConversation(null)
              setConversationsMenuOpen(false)
            }
          }}
          onDelete={async () => {
            if (!pendingDeleteConversation) return
            setDeleteConversationLoading(true)
            try {
              await deleteConversation(pendingDeleteConversation.id)
            } finally {
              setDeleteConversationLoading(false)
            }
          }}
          loading={deleteConversationLoading}
          isNested
          title='Delete conversation'
          description={`Are you sure you want to delete? This removes the chat from your history.`}
          successMessageTitle='Conversation deleted successfully'
          hideToast
        />
      </SheetContent>
    </Sheet>
  )
}
